import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { isAfter, startOfDay, startOfMonth, subDays } from 'date-fns';
import Decimal from 'decimal.js';
import { compact } from 'lodash-es';
import { InvoiceDisplayStatusV2 } from 'anchor-scout-common/types';
import { Divider, Spinner, TableList, TextButton, Typography, WithInfoIcon } from 'seagull';
import GenericError from '~/components/GenericError/GenericError';
import { routes } from '~/constants';
import { useIoContext, useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { MoneyModel, PayoutSummaryStatus, PayoutsSummaryModel, PayoutsSummaryPayoutModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import GraphWidgetBottomCard from '../GraphWidget/GraphWidgetBottomCard';
import GraphWidgetLayout from '../GraphWidget/GraphWidgetLayout';
import GraphWidgetTopCard from '../GraphWidget/GraphWidgetTopCard';
import { usePayoutsSummary } from './hooks/usePayoutsSummary';
import styles from './PayoutsSummaryWidget.module.scss';
const renderDivider = ()=>/*#__PURE__*/ _jsx(Divider, {
        margin: 0
    });
const renderCell = (value, testId)=>({
        value: /*#__PURE__*/ _jsx(Typography, {
            testId: testId,
            children: value
        })
    });
const renderHeaderItem = (value)=>({
        value: /*#__PURE__*/ _jsx(Typography, {
            weight: "normal",
            children: value
        })
    });
function getTableData() {
    const config = [
        {
            columnDef: {
                width: [
                    100,
                    200
                ]
            },
            headerItem: renderHeaderItem('Date')
        },
        {
            columnDef: {
                width: [
                    100,
                    200
                ],
                hAlign: 'end'
            },
            headerItem: renderHeaderItem('Amount')
        },
        {
            columnDef: {
                width: 80,
                hAlign: 'end'
            },
            headerItem: renderHeaderItem('Status')
        }
    ];
    return {
        columnDefs: config.map((i)=>i.columnDef),
        headerItems: config.map((i)=>i.headerItem)
    };
}
const PayoutsSummaryTable = (param)=>{
    let { data, testId } = param;
    const { t: tableTexts } = useLocaleKeys('pages.dashboard.payoutsSummaryWidget');
    const { columnDefs, headerItems } = useMemo(()=>getTableData(), []);
    return /*#__PURE__*/ _jsx("div", {
        className: styles.table,
        "data-testid": testId,
        children: /*#__PURE__*/ _jsxs(TableList.Provider, {
            disableMobile: true,
            variant: "minimal",
            columnDefs: columnDefs,
            hPadding: "xs",
            children: [
                /*#__PURE__*/ _jsx(TableList.Header, {
                    items: headerItems,
                    bgColor: "transparent",
                    size: "small"
                }),
                renderDivider(),
                data.mapPayouts((payout, idx, isLast)=>/*#__PURE__*/ _jsxs(Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(TableList.Row, {
                                disableMarginBottom: true,
                                highlightOnHover: true,
                                size: "medium",
                                as: NavLink,
                                to: payout.redirectURL(),
                                items: compact([
                                    renderCell(payout.formattedDate(), "payout-summary-date-".concat(idx)),
                                    renderCell(payout.formattedAmount(), "payout-summary-amount-".concat(idx)),
                                    renderCell(payout.statusText(tableTexts.statusLabels), "payout-summary-status-".concat(idx))
                                ])
                            }),
                            !isLast && renderDivider()
                        ]
                    }, payout.uniqueKey())),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.viewMore,
                    children: [
                        /*#__PURE__*/ _jsx(TextButton, {
                            bi: {
                                name: 'view_more_payouts'
                            },
                            as: NavLink,
                            to: routes.payoutsDashboard,
                            testId: "payouts-summary-view-more",
                            underline: "none",
                            weight: "normal",
                            children: tableTexts.viewMore()
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            skin: "secondary",
                            children: tableTexts.futurePayoutsDisclaimer()
                        })
                    ]
                })
            ]
        })
    });
};
export function getEmptyData(now) {
    const firstOfMonth = startOfMonth(now);
    let dateToAdd = startOfDay(now);
    const payouts = [];
    while(!isAfter(firstOfMonth, dateToAdd) && payouts.length < 5){
        payouts.push(new PayoutsSummaryPayoutModel({
            id: '',
            date: dateToAdd,
            amount: new MoneyModel(new Decimal(1200)),
            status: PayoutSummaryStatus.Paid
        }));
        dateToAdd = subDays(dateToAdd, 1);
    }
    return PayoutsSummaryModel.makeEmpty().addPayouts(payouts);
}
function PlaceholderTable() {
    const { getNow } = useIoContext();
    const emptyData = useMemo(()=>getEmptyData(getNow()), []);
    return /*#__PURE__*/ _jsx(PayoutsSummaryTable, {
        testId: "payouts-summary-table-placeholder",
        data: emptyData
    });
}
const TopCard = (param)=>{
    let { testId, loading, label, info, value, linkTo } = param;
    return /*#__PURE__*/ _jsx(GraphWidgetTopCard, {
        linkTo: linkTo,
        testId: loading ? "".concat(testId, "-loading") : testId,
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.topCard,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    children: /*#__PURE__*/ _jsx(WithInfoIcon, {
                        info: info,
                        children: /*#__PURE__*/ _jsx(Typography, {
                            children: label
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "h5",
                    weight: "normal",
                    testId: "".concat(testId, "-value"),
                    children: loading ? /*#__PURE__*/ _jsx(Spinner, {
                        size: 30,
                        testId: "".concat(testId, "-loading-spinner")
                    }) : value
                })
            ]
        })
    });
};
function getFuturePayoutsLink() {
    const filters = {
        displayStatusV2: [
            InvoiceDisplayStatusV2.Processing
        ]
    };
    const params = new URLSearchParams();
    Object.keys(filters).forEach((key)=>{
        params.set(key, filters[key].join(','));
    });
    return "".concat(routes.invoicesDashboard, "?").concat(params);
}
function PayoutsSummaryWidgetContent(param) {
    let { loading, data } = param;
    const { t: payoutsSummaryWidgetTexts } = useLocaleKeys('pages.dashboard.payoutsSummaryWidget');
    const noData = !data;
    const topCards = [
        /*#__PURE__*/ _jsx(TopCard, {
            testId: "payouts-summary-paid-this-month",
            loading: noData,
            label: payoutsSummaryWidgetTexts.paidThisMonth(),
            info: payoutsSummaryWidgetTexts.infoFundsTransferred(),
            value: data ? data.formattedPaidThisMonth() : '',
            linkTo: routes.payoutsDashboard
        }, "paid"),
        /*#__PURE__*/ _jsx(TopCard, {
            testId: "payouts-summary-future-payouts",
            loading: noData,
            label: payoutsSummaryWidgetTexts.processing(),
            info: payoutsSummaryWidgetTexts.infoFundsInTransit(),
            value: data ? data.formattedFuturePayouts() : '',
            linkTo: getFuturePayoutsLink()
        }, "future")
    ];
    return /*#__PURE__*/ _jsxs(GraphWidgetLayout, {
        title: payoutsSummaryWidgetTexts.title(),
        loading: loading,
        bottomHeight: "l",
        children: [
            topCards,
            /*#__PURE__*/ _jsx(GraphWidgetBottomCard, {
                testId: "payouts-summary",
                loading: noData,
                loadingMsg: payoutsSummaryWidgetTexts.loadingMsg(),
                empty: data && data.isEmpty(),
                emptyMsg: payoutsSummaryWidgetTexts.emptyMsg(),
                loadingOrEmptyBg: ()=>/*#__PURE__*/ _jsx(PlaceholderTable, {}),
                children: data && /*#__PURE__*/ _jsx(PayoutsSummaryTable, {
                    testId: "payouts-summary-table",
                    data: data
                })
            })
        ]
    });
}
export default function PayoutsSummaryWidget() {
    const { payoutsSummary, isLoading, isFetching, error } = usePayoutsSummary({
        refreshOnHookMount: true
    });
    const isMemberUser = useStore(selectors.user.isMemberUser);
    if (isMemberUser) return null;
    if (error) return /*#__PURE__*/ _jsx(GenericError, {
        testId: "widget-error"
    });
    if (isLoading) return /*#__PURE__*/ _jsx(PayoutsSummaryWidgetContent, {
        loading: true
    });
    return /*#__PURE__*/ _jsx(PayoutsSummaryWidgetContent, {
        data: payoutsSummary,
        loading: isFetching
    });
}
